import { createUrl } from "@/utilities/api";
import {
  DataFileExtensions,
  DataFileReferencesResponse,
  DataFilesQueryParams,
  PaginatedDataFiles,
  PublicDataFileResponse,
  Tags,
  TagType,
} from "./types";
import { BuilderType } from "@/types/base";
import { convertToFormData } from "@/utilities/forms";

const DATAFILES_URL = "/datablocks/files/";

const dataBlocksService = (builder: BuilderType<Tags>) => ({
  getDataFiles: builder.query<PaginatedDataFiles, DataFilesQueryParams>({
    query: (queryParams) => ({
      url: createUrl(DATAFILES_URL, queryParams),
      method: "GET",
    }),
    providesTags: (result): TagType[] =>
      result
        ? [
            ...(result?.results || result).map(
              (i): TagType => ({
                type: "DataFiles",
                id: i.id,
              }),
            ),
            { type: "DataFile", id: "PARTIAL-LIST" },
          ]
        : [{ type: "DataFiles" }],
  }),
  getDataFileExtensions: builder.query<DataFileExtensions, void>({
    query: () => ({
      url: `${DATAFILES_URL}extensions/`,
      method: "GET",
    }),
    providesTags: (result): TagType[] =>
      result
        ? [
            ...result.extensions.map(
              (i): TagType => ({
                type: "DataFileExtensions",
                id: i,
              }),
            ),
            { type: "DataFileExtension", id: "PARTIAL-LIST" },
          ]
        : [{ type: "DataFileExtensions" }],
  }),
  createDataFile: builder.mutation({
    query: (data: Record<string, any>) => ({
      url: createUrl(DATAFILES_URL),
      method: "POST",
      data: convertToFormData(data),
    }),
    invalidatesTags: [{ type: "DataFiles" }],
  }),
  deleteDataFile: builder.mutation({
    query: (fileId) => ({
      url: `${DATAFILES_URL}${fileId}/`,
      method: "DELETE",
    }),
    invalidatesTags: [{ type: "DataFiles" }],
  }),
  getDataFileReferences: builder.query<DataFileReferencesResponse, number>({
    query: (id) => ({
      url: `${DATAFILES_URL}${id}/references/`,
      method: "GET",
    }),
    providesTags: (result, _, args: number): TagType[] =>
      result
        ? [
            { type: "DataFileReference", id: args },
            { type: "DataFileReferences" },
          ]
        : [{ type: "DataFileReferences" }],
  }),
  getPublicDataFile: builder.query<PublicDataFileResponse, string>({
    query: (uuid) => ({
      url: createUrl(`${DATAFILES_URL}public/`, { uuid }),
      method: "GET",
    }),
  }),
});

export default dataBlocksService;
